import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { VolumeOffIcon, VolumeUpIcon } from '@heroicons/react/outline';
import { ChevronLeftIcon } from '@heroicons/react/solid';
import { Affix, Button, MenuProps, Slider, Spin } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { CourseDummy } from 'assets';
import EnterFullScreen from 'assets/Icons/fullscreen/EnterFullScreen';
import ExitFullScreen from 'assets/Icons/fullscreen/ExitFullScreen';
import PauseIcon from 'assets/Icons/PauseIcon';
import ForwardIcon from 'assets/Icons/PdfReaderVideoPlayer/ForwardIcon';
import PlayNextIcon from 'assets/Icons/PdfReaderVideoPlayer/PlayNextIcon';
import RewindIcon from 'assets/Icons/PdfReaderVideoPlayer/RewindIcon';
import SpeedIcon from 'assets/Icons/PdfReaderVideoPlayer/SpeedIcon';
import VideoIndex from 'assets/Icons/PdfReaderVideoPlayer/VideoIndex';
import PlayIcon from 'assets/Icons/PlayIcon';
import ShareIconVideo from 'assets/Icons/ShareIconVideo';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import dynamic from 'next/dynamic';
import Image, { StaticImageData } from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import AutoNextOverlay from '@/components/PdfReader/VideoSummary/AutoNextOverlay';
import PlayerTooltip from '@/components/PdfReader/VideoSummary/PlayerTooltip';
import VideoIndexMenu from '@/components/PdfReader/VideoSummary/VideoIndexMenu';
import { notify } from '@/components/shared/basic/notify';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import { authModalControl } from '@/atoms/authModalControl';
import { shareModalVisibleState } from '@/atoms/modalAtom';
import { user } from '@/atoms/user';
import { ChapterVideoPlayer } from '@/utils/interfaces/Library/Pdf';
import { User } from '@/utils/interfaces/User';

const ShareButton = dynamic(() => import('@/components/PdfReader/ShareButton'));

const VideoPlayer = ({
  src,
  poster,
  currentChapterTitle,
  bookName,
  chaptersList,
  setCurrent,
  chapterNumber,
  currentChapterDuration,
  onBack,
}: {
  src: string;
  poster?: StaticImageData | string;
  currentChapterTitle?: string;
  bookName?: string;
  chapterNumber?: number;
  chaptersList?: ChapterVideoPlayer[];
  setCurrent: (chapter: ChapterVideoPlayer) => void;
  currentChapterDuration?: number;
  /** When provided, the back button calls this instead of navigating to home. */
  onBack?: () => void;
}) => {
  const [volume, setVolume] = useState(0.8);
  const [duration, setDuration] = useState({
    currentTime: 0,
    totalTime: 0,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progressNode, setProgressNode] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const [openSpeed, setOpenSpeed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastVolume, setLastVolume] = useState(0.8);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [nextTitle, setNextTitle] = useState('');
  const [, setVisible] = useRecoilState(authModalControl);
  const [, setIsModalVisibleShare] = useRecoilState(shareModalVisibleState);

  const userData: User | undefined = useRecoilValue(user);

  const [showControls, setShowControls] = useState(false);
  const hideTimeout = useRef<NodeJS.Timeout | null>(null);
  const screens = useBreakpoint();
  const router = useRouter();

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [currentSpeed, setCurrentSpeed] = useState<number>(1.0);
  const playerRef = useRef<any>(null);
  const { book_id } = router.query;

  const speeds = [0.5, 0.75, 1.0, 1.25, 1.5];

  const iconWidthAndHeight = screens.md ? 32 : screens.lg ? 40 : 16;

  const resetPlayerState = () => {
    setVolume(0.8);
    setDuration({
      currentTime: 0,
      totalTime: 0,
    });
    setIsPlaying(false);
    setIsFullscreen(false);
    setOpen(false);
    setShowControls(false);
  };

  const playNextVideo = () => {
    if (!chaptersList?.length || chapterNumber == null) return;

    const currentIndex = chaptersList.findIndex(
      (c) => c.chapterNumber === chapterNumber,
    );

    if (currentIndex >= 0 && currentIndex < chaptersList.length - 1) {
      const nextChapter = chaptersList[currentIndex + 1];

      if (typeof window !== 'undefined') {
        localStorage.setItem('video-autonext', 'true');
      }

      resetPlayerState();
      setOverlayVisible(false);
      setCurrent(nextChapter as ChapterVideoPlayer);
    }

    setOverlayVisible(false);
  };

  useEffect(() => {
    if (!videoRef.current || !containerRef.current) return;

    let active = true;
    let player: any = null;
    let handleKeydown: any = null;
    let handleTap: any = null;
    let videoEl: any = null;

    import('plyr').then((module) => {
      const Plyr = module.default;
      if (!active || !videoRef.current || !containerRef.current) return;

      player = new Plyr(videoRef.current, {
        controls: ['progress'],
        fullscreen: {
          enabled: true,
          fallback: true,
          iosNative: true,
          container: '#video-container',
        },
      });

      playerRef.current = player;

      const progressKey = `video-progress-${book_id}`;
      const settingsKey = 'video-settings';

      if (typeof window !== 'undefined') {
        const isAutoNext = localStorage.getItem('video-autonext') === 'true';

        if (!isAutoNext) {
          const savedTime = localStorage.getItem(progressKey);
          if (savedTime) {
            player.once('loadedmetadata', () => {
              player.currentTime = parseFloat(savedTime);
            });
          }
        } else {
          player.once('loadedmetadata', () => {
            player.currentTime = 0;
            player.play();
          });
          localStorage.removeItem('video-autonext');
        }

        const savedSettingsStr = localStorage.getItem(settingsKey);
        if (savedSettingsStr) {
          try {
            const allSettings = JSON.parse(savedSettingsStr) || {};
            const { volume, speed, captions } =
              allSettings[book_id as string] || {};

            if (volume !== undefined) player.volume = volume;
            if (speed !== undefined) player.speed = speed;
            if (captions) player.toggleCaptions(true);
          } catch {
            /* ignore bad JSON */
          }
        }

        const handleTimeUpdate = () => {
          localStorage.setItem(progressKey, player.currentTime.toString());
        };

        const saveSettings = () => {
          const savedSettingsStr = localStorage.getItem(settingsKey);
          const allSettings = savedSettingsStr
            ? JSON.parse(savedSettingsStr)
            : {};

          allSettings[book_id as string] = {
            volume: player.volume,
            speed: player.speed,
            // captions: player.language,
          };

          localStorage.setItem(settingsKey, JSON.stringify(allSettings));
        };

        player.on('timeupdate', handleTimeUpdate);
        player.on('volumechange', saveSettings);
        player.on('ratechange', saveSettings);
        player.on('languagechange', saveSettings);
      }

      handleKeydown = (e: KeyboardEvent) => {
        switch (e.key.toLowerCase()) {
          case ' ':
            e.preventDefault();
            player.togglePlay();
            break;
          case 'arrowright':
            player.forward(10);
            break;
          case 'arrowleft':
            player.rewind(10);
            break;
          case 'f':
            player.fullscreen.toggle();
            break;
          case 'm':
            player.muted = !player.muted;
            break;
        }
      };
      window.addEventListener('keydown', handleKeydown);

      let lastTap = 0;
      handleTap = () => {
        const now = Date.now();
        if (now - lastTap < 300) {
          const fs = player.fullscreen;
          fs.active ? fs.exit() : fs.enter();
        }
        lastTap = now;
      };

      videoEl = player.elements.container;
      videoEl?.addEventListener('click', handleTap);

      player.on('ratechange', () => setCurrentSpeed(player.speed));
      player.on('timeupdate', () => {
        setDuration({
          currentTime: player.currentTime,
          totalTime: player.duration,
        });
        setVolume(player.volume);
      });

      player.on('play', () => setIsPlaying(true));
      player.on('pause', () => setIsPlaying(false));

      player.on('enterfullscreen', async () => {
        setIsFullscreen(true);
        try {
          if (window.screen.orientation) {
            await (window.screen.orientation as any).lock('landscape');
          }
        } catch (err) {
          console.warn('Orientation lock not supported:', err);
        }
      });

      player.on('exitfullscreen', async () => {
        setIsFullscreen(false);
        try {
          if (window.screen.orientation?.unlock) {
            window.screen.orientation.unlock();
          }
        } catch (err) {
          console.warn('Orientation unlock not supported:', err);
        }
      });

      player.once('ready', () => {
        const progressEl = (player.elements as any)
          .progress as HTMLElement | null;
        if (progressEl && !progressEl.querySelector('.plyr__custom-buffer')) {
          const bufferEl = document.createElement('div');
          bufferEl.className = 'plyr__custom-buffer';
          bufferEl.style.position = 'absolute';
          bufferEl.style.top = '0';
          bufferEl.style.left = '0';
          bufferEl.style.height = '100%';
          bufferEl.style.width = '0%';
          bufferEl.style.background = 'rgba(255,255,255,0.3)';
          bufferEl.style.pointerEvents = 'none';
          bufferEl.style.zIndex = '0';

          progressEl.style.position = 'relative';
          progressEl.prepend(bufferEl);

          const updateBuffer = () => {
            if (
              videoRef.current &&
              videoRef.current.buffered?.length > 0 &&
              videoRef.current.duration > 0
            ) {
              const bufferedEnd = videoRef.current.buffered.end(
                videoRef.current.buffered.length - 1,
              );
              const percent = (bufferedEnd / videoRef.current.duration) * 100;
              bufferEl.style.width = `${percent}%`;
            }
          };

          videoRef.current?.addEventListener('progress', updateBuffer);
          videoRef.current?.addEventListener('loadedmetadata', updateBuffer);

          (player as any)._bufferCleanup = () => {
            videoRef.current?.removeEventListener('progress', updateBuffer);
            videoRef.current?.removeEventListener('loadedmetadata', updateBuffer);
          };
        }
      });
    });

    return () => {
      active = false;
      if (handleKeydown) {
        window.removeEventListener('keydown', handleKeydown);
      }
      if (videoEl && handleTap) {
        videoEl.removeEventListener('click', handleTap);
      }
      if (player) {
        if (player._bufferCleanup) {
          player._bufferCleanup();
        }
        player.destroy();
      }
      playerRef.current = null;
    };
  }, [src, book_id]);

  useEffect(() => {
    if (!playerRef.current) return;

    const plyrControls = playerRef.current.elements.controls;
    if (!plyrControls) return;

    const progressEl = plyrControls.querySelector(
      '.plyr__progress',
    ) as HTMLElement | null;

    if (progressEl && !progressNode) {
      setProgressNode(progressEl);
    }
  }, [playerRef.current, progressNode, currentChapterTitle]);

  useEffect(() => {
    const player = playerRef.current;
    if (!player || !chaptersList?.length || chapterNumber == null) return;

    const handleEnded = () => {
      const currentIndex = chaptersList.findIndex(
        (c) => c.chapterNumber === chapterNumber,
      );

      if (currentIndex !== -1 && currentIndex < chaptersList.length - 1) {
        const nextChapter = chaptersList[currentIndex + 1];
        setNextTitle(nextChapter?.name as string);
        setOverlayVisible(true);
      }
    };

    player.once('ended', handleEnded);

    return () => {
      player.off('ended', handleEnded);
    };
  }, [chapterNumber, chaptersList]);

  const isIphone = (): boolean => {
    const parser = new UAParser();
    const device = parser.getDevice();

    return device.model === 'iPhone';
  };

  const handleVolumeChange = (value: number) => {
    if (isIphone()) {
      if (value === 0) {
        playerRef.current!.muted = true;
        setLastVolume(volume);
        setVolume(0);
      } else {
        playerRef.current!.muted = false;
        setVolume(lastVolume || 1);
      }
      return;
    }

    playerRef.current!.volume = value;
    playerRef.current!.muted = value === 0;
    setVolume(value);
  };

  const stopEvent = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const formatDuration = (
    seconds?: number,
    format: 'hh:mm:ss' | 'mm:ss' | 'default' = 'default',
  ) => {
    if (!seconds) return;

    const duration = moment.duration(seconds, 'seconds');
    const hours = duration.hours();
    const minutes = duration.minutes();
    const secs = duration.seconds();

    switch (format) {
      case 'hh:mm:ss':
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(
          secs,
        ).padStart(2, '0')}`;

      case 'mm:ss':
        return `${String(minutes + hours * 60).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

      case 'default':
      default:
        return [
          hours ? `${hours}h` : null,
          minutes ? `${minutes}m` : null,
          secs ? `${secs}s` : null,
        ]
          .filter(Boolean)
          .join(' ');
    }
  };

  const items: MenuProps['items'] = [
    ...(chaptersList ?? []).map((chapter, index) => ({
      key: `${chapter._id}-${index}`,
      label: (
        <div
          onClick={() => {
            resetPlayerState();
            setCurrent(chapter);
          }}
          className='flex items-center gap-4 py-2 group'
        >
          <div className='relative min-w-[12.5rem] h-[7.0625rem] rounded overflow-hidden'>
            <Image
              src={chapter.videoThumbnailUrl || CourseDummy}
              alt={`chapter ${chapter.chapterNumber}`}
              fill
              sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 200px'
              className='object-cover rounded-[4px]'
            />
            <ShouldRender check={chapter.name === currentChapterTitle}>
              <div className='absolute inset-0 flex items-center justify-center  text-white/80 text-sm font-medium'>
                <div className='flex items-center justify-center gap-2.5 pt-[0.3125rem] pb-[0.3125rem] px-2 rounded-lg h-fit w-fit  bg-black/60 text-white/[.80] text-[10px] lg:text-sm font-medium leading-[1.375rem]'>
                  Playing
                </div>
              </div>
            </ShouldRender>
            <ShouldRender check={chapter.name !== currentChapterTitle}>
              <div className='absolute inset-0 flex items-center justify-center  text-white/80 text-sm font-medium'>
                <button className='w-10 h-10 flex items-center justify-center rounded-full bg-black/60 group-hover:bg-black/70 transition'>
                  <div className='ml-1'>
                    <PlayIcon width={11} height={14} fill='white' />
                  </div>
                </button>
              </div>
            </ShouldRender>
          </div>
          <div className='flex flex-col justify-center min-w-[292px]'>
            <div className='flex items-center gap-2 text-[#9f9ea6] text-xs'>
              <span>Chapter {chapter.chapterNumber}</span>•
              <span>{formatDuration(chapter.videoMetaData?.duration)}</span>
            </div>
            <div className='text-white leading-6 line-clamp-1 max-w-[250px]'>
              {chapter.name}
            </div>
          </div>
        </div>
      ),
    })),
  ];

  const speedItems: MenuProps['items'] = [
    ...speeds.map((speed) => ({
      key: `${speed}`,
      label: (
        <div
          onClick={() => {
            setCurrentSpeed(speed);
            if (playerRef.current) {
              playerRef.current.speed = speed;
            }
          }}
          className='flex items-center py-1 px-2 lg:py-3 lg:min-w-[200px] cursor-pointer text-xs lg:text-sm'
        >
          <div className='w-4 flex items-center justify-center'>
            {currentSpeed === speed && (
              <CheckOutlined className='text-white text-xs' />
            )}
          </div>
          <span
            className={
              currentSpeed === speed
                ? 'ml-2 text-white font-bold'
                : 'ml-2 text-white/70 font-normal'
            }
          >
            {speed.toFixed(2).replace(/\.00/, '')}X
          </span>
        </div>
      ),
    })),
  ];

  const leftControls = useMemo(
    () => [
      {
        key: 'play / pause',
        onClick: () => playerRef.current?.togglePlay(),
        render: () =>
          isPlaying ? (
            <PauseIcon
              width={screens.lg ? 20 : screens.md ? 16 : 14}
              height={screens.lg ? 20 : screens.md ? 18 : 14}
              fill='white'
            />
          ) : (
            <PlayIcon
              width={screens.lg ? 20 : screens.md ? 16 : 14}
              height={screens.lg ? 20 : screens.md ? 18 : 14}
              fill='white'
            />
          ),
      },
      {
        key: 'rewind',
        onClick: () => playerRef.current?.rewind(10),
        render: () => (
          <RewindIcon width={iconWidthAndHeight} height={iconWidthAndHeight} />
        ),
      },
      {
        key: 'forward',
        onClick: () => playerRef.current?.forward(10),
        render: () => (
          <ForwardIcon width={iconWidthAndHeight} height={iconWidthAndHeight} />
        ),
      },
    ],
    [isPlaying, items, isFullscreen, setOpen, open],
  );
  const hasNextChapter =
    chaptersList &&
    chapterNumber != null &&
    chaptersList.findIndex((c) => c.chapterNumber === chapterNumber) <
    chaptersList.length - 1;

  const rightControls = [
    ...(hasNextChapter
      ? [
        {
          key: 'next',
          render: () => (
            <PlayNextIcon
              width={screens.lg ? 40 : screens.md ? 30 : 20}
              height={screens.lg ? 40 : screens.md ? 30 : 20}
            />
          ),
          onClick: playNextVideo,
        },
      ]
      : []),
    ...(screens.md
      ? [
        {
          key: 'index',
          render: () => (
            <VideoIndexMenu
              items={items}
              isFullscreen={isFullscreen}
              buttonName='Video Index'
              title='Index'
              setOpen={(flag) => {
                if (!screens.lg && isFullscreen) {
                  if (playerRef.current) {
                    playerRef?.current?.fullscreen?.toggle?.();
                  }
                } else {
                  setOpen(flag);
                }
              }}
              open={open}
              placement='topLeft'
              icon={
                <VideoIndex
                  width={screens.lg ? 32 : screens.md ? 24 : 16}
                  height={screens.lg ? 32 : screens.md ? 24 : 16}
                />
              }
            />
          ),
        },
      ]
      : []),
    // {
    //   key: 'language',
    //   render: () => (
    //     <Button
    //       type='text'
    //       className='!w-16 !h-16 p-0 !rounded-full !flex !items-center !justify-center hover:!bg-[#ffffff4d]'
    //     >
    //       <LanguageIcon />
    //     </Button>
    //   ),
    // },
    {
      key: 'speed',
      render: () => (
        <VideoIndexMenu
          items={speedItems}
          buttonName='Speed'
          isFullscreen={isFullscreen}
          setOpen={setOpenSpeed}
          title='Speed'
          open={openSpeed}
          icon={
            <SpeedIcon
              width={screens.lg ? 40 : screens.md ? 28 : 20}
              height={screens.lg ? 40 : screens.md ? 28 : 20}
            />
          }
          placement='topLeft'
        />
      ),
    },
    {
      key: 'fullscreen',

      onClick: () => {
        if (playerRef.current) {
          playerRef?.current?.fullscreen?.toggle?.();
        }
      },
      render: () =>
        isFullscreen ? (
          <ExitFullScreen
            width={screens.lg ? 30 : screens.md ? 24 : 16}
            height={screens.lg ? 30 : screens.md ? 24 : 16}
          />
        ) : (
          <EnterFullScreen
            width={screens.lg ? 30 : screens.md ? 24 : 16}
            height={screens.lg ? 30 : screens.md ? 24 : 16}
          />
        ),
    },
  ];

  const resetHideTimer = () => {
    setShowControls(true);
    if (hideTimeout.current) clearTimeout(hideTimeout.current);
    hideTimeout.current = setTimeout(() => setShowControls(false), 2000);
  };

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const handleMouseMove = () => resetHideTimer();
    container.addEventListener('mousemove', handleMouseMove);
    resetHideTimer();

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      if (hideTimeout.current) clearTimeout(hideTimeout.current);
    };
  }, []);

  const showLayout = useMemo(() => {
    return open || showControls || openSpeed || !isPlaying;
  }, [open, showControls, openSpeed]);

  useEffect(() => {
    if (!videoRef.current) return;

    setLoading(true);

    const videoEl = videoRef.current;

    const handleCanPlay = () => {
      setLoading(false);
    };

    videoEl.addEventListener('canplay', handleCanPlay);

    videoEl.load();
    videoEl.play().catch(() => { });

    return () => {
      videoEl.removeEventListener('canplay', handleCanPlay);
    };
  }, [src]);

  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError(...args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const currentChapter = useMemo(() => {
    return (chaptersList ?? []).find(
      (chapter) => chapter.chapterNumber === chapterNumber,
    );
  }, [chaptersList, chapterNumber]);

  const handleRestoreProgress = (bookId?: string) => {
    if (!bookId) {
      router.replace('/');
      return;
    }

    const progressKey = `book-progress-${bookId}`;

    const savedProgress = localStorage.getItem(progressKey);

    if (savedProgress) {
      try {
        const { bookId: storedBookId } = JSON.parse(savedProgress);

        localStorage.removeItem(progressKey);

        router.push(
          `/pdf/${storedBookId}?chapterPageNumber=${currentChapter?.startPage || 1}`,
        );
        return;
      } catch {
        localStorage.removeItem(progressKey);
      }
    }

    router.replace('/');
  };
  const handleShareClick = () => {
    playerRef.current?.togglePlay();
    if (!userData) {
      setVisible((prev) => ({ ...prev, login: true }));
      notify('Please login to share', 'warning');
      setIsModalVisibleShare(false);
    } else {
      setIsModalVisibleShare((prev) => !prev);
    }
  };

  const renderShareButton = (
    <>
      <PlayerTooltip key='Share' title='Share'>
        <ShouldRender check={!isFullscreen}>
          <ShareButton
            isLoggedIN={!!userData}
            noBg={screens.lg && true}
            type='video-book'
            togglePlayer={() => playerRef.current?.pause()}
          />
        </ShouldRender>

        <ShouldRender check={isFullscreen}>
          <Button
            onClick={() => handleShareClick()}
            icon={
              <ShareIconVideo
                width={!screens.md ? 28 : 40}
                height={!screens.md ? 28 : 40}
              />
            }
            className={clsx(
              'p-0 lg:py-3 lg:px-5 h-10',
              'border-none rounded-lg bg-transparent text-sm flex items-center font-semibold text-white hover:opacity-80',
            )}
          />
          <ShareButton
            isFullScreen={isFullscreen}
            isLoggedIN={!!userData?.zl_uid}
            type='no-icon'
            isPdfReader={true}
            productId={book_id as string}
            togglePlayer={() => playerRef.current?.pause()}
          />
        </ShouldRender>
      </PlayerTooltip>
    </>
  );

  return (
    <div className='h-full no-select'>
      <ShouldRender check={!screens.lg}>
        <div className='relative flex items-center bg-black h-12'>
          <div
            onClick={() => onBack ? onBack() : handleRestoreProgress(book_id as string)}
            className='absolute left-0 flex items-center gap-2.5 p-2.5 text-white'
          >
            <ChevronLeftIcon className='w-6 h-6' />
          </div>

          <div className='flex-1 text-center px-12 truncate  text-white text-sm font-semibold leading-[1.375rem]'>
            {bookName}
          </div>
          <div className='absolute right-0 flex items-center gap-2.5 p-2.5 text-white'>
            {renderShareButton}
          </div>
        </div>
      </ShouldRender>

      <div
        id='video-container'
        ref={containerRef}
        className='relative w-full  overflow-hidden bg-black'
      >
        <Affix offsetTop={0} className='w-full z-10'>
          <motion.div
            animate={showLayout ? { y: 0, opacity: 1 } : { y: -80, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className={clsx(
              isFullscreen ? 'hidden md:flex' : 'hidden lg:flex',
              'absolute left-0 right-0 top-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent items-center justify-between',
            )}
          >
            <Button
              onClick={() => onBack ? onBack() : handleRestoreProgress(book_id as string)}
              type='text'
              className='flex items-center jus py-8'
            >
              <div className='flex gap-x-3 items-center '>
                <div className='w-16 h-16 flex items-center p-3 justify-center'>
                  <ChevronLeftIcon className='' />
                </div>
                <div className='text-white/80 flex flex-col items-start justify-start '>
                  <span className=' text-white text-xs leading-5 font-light'>
                    {bookName}
                  </span>
                  <div className=' text-white font-semibold leading-6'>
                    {currentChapterTitle}
                  </div>
                </div>
              </div>
            </Button>
            <div className='flex items-center gap-2.5 p-3 text-white'>
              {renderShareButton}
            </div>
          </motion.div>
        </Affix>
        <Spin
          spinning={loading}
          indicator={<LoadingOutlined spin />}
          className={clsx(
            'w-full min-h-[220px] md:min-h-[50vh] lg:min-h-[100vh] customBufferPlayer',
          )}
          size={!screens.md ? 'small' : 'large'}
        >
          <video
            ref={videoRef}
            key={src}
            playsInline
            onError={(e) => {
              const target = e.currentTarget as HTMLVideoElement;
              if (
                target.error?.code === target.error?.MEDIA_ERR_SRC_NOT_SUPPORTED
              ) {
                notify('You are not authenticated', 'warning');
                const redirectUrl = '/';

                router.replace(redirectUrl);
              }
            }}
            className={clsx('max-h-dvh')}
            poster={typeof poster === 'string' ? poster : poster?.src}
          >
            <source src={src} type='video/mp4' />
          </video>
        </Spin>
        <AutoNextOverlay
          visible={overlayVisible}
          nextTitle={nextTitle}
          onCancel={() => setOverlayVisible(false)}
          onFinish={playNextVideo}
        />

        {showLayout && !loading && (
          <div className='absolute inset-0 flex items-center justify-center bg-black/30'>
            <AnimatePresence mode='wait' initial={false}>
              {!isPlaying ? (
                <motion.button
                  key='play'
                  onClick={(e) => {
                    stopEvent(e);
                    playerRef.current?.play()?.then(() => {
                      if (playerRef.current?.volume !== undefined) {
                        playerRef.current.volume = volume;
                      }
                    });
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className='w-8 h-8 md:w-14 md:h-14 lg:w-20 lg:h-20 -translate-y-2 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30'
                >
                  <div className='ml-1 lg:ml-2'>
                    <PlayIcon
                      width={screens.lg ? 30 : screens.md ? 20 : 11}
                      height={screens.lg ? 30 : screens.md ? 20 : 14}
                      fill='white'
                    />
                  </div>
                </motion.button>
              ) : (
                <motion.button
                  key='pause'
                  onClick={(e) => {
                    stopEvent(e);
                    (playerRef.current?.pause() as any)?.then(() => {
                      if (playerRef.current?.volume !== undefined) {
                        playerRef.current.volume = volume;
                      }
                    });
                  }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  className='w-8 h-8 md:w-14 md:h-14 lg:w-20 lg:h-20 -translate-y-2 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30'
                >
                  <PauseIcon
                    width={screens.lg ? 30 : screens.md ? 20 : 11}
                    height={screens.lg ? 30 : screens.md ? 20 : 14}
                    fill='white'
                  />
                </motion.button>
              )}
            </AnimatePresence>
          </div>
        )}

        <Affix offsetBottom={0} className='w-full z-10'>
          {showLayout && (
            <motion.div
              initial={{ y: 80, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 80, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeInOut' }}
              className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent'
            >
              <div className='flex items-end px-3 md:px-4 relative z-0'>
                <div
                  ref={(el) => {
                    if (
                      el &&
                      progressNode &&
                      !el.querySelector('.plyr__progress')
                    ) {
                      requestAnimationFrame(() => {
                        if (
                          el &&
                          progressNode &&
                          !el.querySelector('.plyr__progress')
                        ) {
                          el.appendChild(progressNode);
                        }
                      });
                    }
                  }}
                  className='w-full pb-2 lg:pb-5'
                />
                <div className='ml-2 flex items-end translate-y-3 lg:translate-y-0  h-full whitespace-nowrap text-xs lg:text-sm text-white'>
                  {formatDuration(duration.currentTime, 'hh:mm:ss') ?? '00:00'}{' '}
                  /{' '}
                  {formatDuration(duration.totalTime, 'hh:mm:ss') ??
                    formatDuration(currentChapterDuration, 'hh:mm:ss')}
                </div>
              </div>

              <div className='flex justify-between items-center'>
                <div className='flex items-center gap-x-1 lg:gap-3 px-3 md:px-4 pt-4 lg:pb-4'>
                  {leftControls.map((control) => (
                    <PlayerTooltip key={control.key} title={control.key}>
                      <button
                        key={control.key}
                        aria-label={control.key}
                        onClick={control.onClick}
                        className='w-6 h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 !rounded-full !flex !items-center !justify-center hover:!bg-[#ffffff4d]'
                      >
                        {control.render()}
                      </button>
                    </PlayerTooltip>
                  ))}

                  <div className='flex items-center  gap-3 customPdfVideoPlayer'>
                    {playerRef?.current?.muted ? (
                      <PlayerTooltip title='Unmute'>
                        <button
                          key='Unmute'
                          aria-label='Unmute'
                          onClick={() => handleVolumeChange(0.5)}
                          className='w-6 h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 !rounded-full !flex !items-center !justify-center hover:!bg-[#ffffff4d]'
                        >
                          <VolumeOffIcon
                            type='button'
                            className='w-4 h-4 md:w-6 md:h-6 lg:h-[30px] lg:w-[30px] text-white cursor-pointer hover:!bg-[#ffffff4d]'
                          />
                        </button>
                      </PlayerTooltip>
                    ) : (
                      <PlayerTooltip title='Mute'>
                        <button
                          key='Mute'
                          aria-label='Mute'
                          onClick={() => handleVolumeChange(0)}
                          className='w-6 h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 !rounded-full !flex !items-center !justify-center hover:!bg-[#ffffff4d]'
                        >
                          <VolumeUpIcon className='w-4 h-4 md:w-7 md:h-7 lg:h-[30px] lg:w-[30px] text-white cursor-pointer' />
                        </button>
                      </PlayerTooltip>
                    )}
                    <ShouldRender check={!isIphone()}>
                      <PlayerTooltip title='Volume'>
                        <Slider
                          min={0}
                          max={1}
                          step={0.01}
                          value={volume}
                          aria-label='Volume control'
                          onChange={handleVolumeChange}
                          tooltip={{ formatter: null }}
                          className='w-12 md:w-16 lg:w-20'
                        />
                      </PlayerTooltip>
                    </ShouldRender>
                  </div>
                </div>

                <div className='flex items-center gap-x-1 lg:gap-3 px-3 md:px-4 pt-4 lg:pb-4'>
                  {rightControls.map((control) =>
                    control.onClick ? (
                      <PlayerTooltip key={control.key} title={control.key}>
                        <button
                          onClick={control.onClick}
                          aria-label={control.key}
                          className='w-6 h-6 md:w-12 md:h-12 lg:w-16 lg:h-16 !rounded-full !flex !items-center !justify-center hover:!bg-[#ffffff4d]'
                        >
                          {control.render()}
                        </button>
                      </PlayerTooltip>
                    ) : (
                      <div key={control.key}>{control.render()}</div>
                    ),
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </Affix>
      </div>
      <ShouldRender check={!screens.lg}>
        <div className='flex flex-col h-full'>
          <div className='flex flex-col justify-center items-start gap-0.5 self-stretch py-3 px-3 md:px-4 bg-black'>
            <div className='text-white text-start line-clamp-1  text-sm font-medium leading-[1.375rem]'>
              {currentChapterTitle}
            </div>
            <div className='flex items-start gap-2'>
              <div className='text-[#9f9ea6] text-center text-xs leading-5'>
                Chapter {chapterNumber}
              </div>
              <div className='text-[#9f9ea6] text-center text-xs leading-5'>
                •
              </div>
              <div className='text-[#9f9ea6] text-center text-xs leading-5'>
                {formatDuration(currentChapterDuration)}
              </div>
            </div>
          </div>

          <div className='bg-grayZl-950 h-full'>
            <div className='flex items-center gap-2 self-stretch p-4'>
              <div className='text-white text-center font-semibold leading-6'>
                Video Book Index
              </div>
              <div className='flex flex-col justify-center items-center gap-2.5 py-px px-2 rounded-full bg-[#ff5612]/[.24] text-[#ffcca8] text-center text-sm font-semibold leading-[1.375rem]'>
                {chaptersList?.length || 0}
              </div>
            </div>
            <div className='flex flex-col gap-y-4'>
              {...(chaptersList ?? []).map((chapter, index) => (
                <div
                  key={`${chapter._id}-${index}`}
                  onClick={() => {
                    resetPlayerState();
                    setCurrent(chapter);
                  }}
                  className='flex items-center gap-x-3  px-4 group'
                >
                  <div className='relative min-w-[120px] h-[68px] lg:w-[12.5rem] lg:h-[7.0625rem] rounded overflow-hidden'>
                    <Image
                      src={chapter.videoThumbnailUrl || CourseDummy}
                      alt={`chapter ${chapter.chapterNumber}`}
                      fill
                      sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 200px'
                      className='object-cover rounded-[2px] lg:rounded-[4px]'
                    />
                    <ShouldRender check={chapter.name === currentChapterTitle}>
                      <div className='absolute inset-0 flex items-center justify-center  text-white/80 text-sm font-medium'>
                        <div className='flex items-center justify-center gap-2.5 py-0.5 px-2 rounded-lg h-fit w-fit  bg-black/60 text-white/[.80] text-[10px] lg:text-sm font-medium leading-[1.375rem]'>
                          Playing
                        </div>
                      </div>
                    </ShouldRender>
                    <ShouldRender check={chapter.name !== currentChapterTitle}>
                      <div className='absolute inset-0 flex items-center justify-center  text-white/80 text-sm font-medium'>
                        <button className='w-8 h-8 flex items-center justify-center rounded-full bg-black/60 group-hover:bg-black/70 transition'>
                          <div className='ml-1'>
                            <PlayIcon width={11} height={14} fill='white' />
                          </div>
                        </button>
                      </div>
                    </ShouldRender>
                  </div>
                  <div className='flex flex-col justify-center min-w-[292px]'>
                    <div className='flex items-center gap-2 text-[#9f9ea6] leading-5 text-xs'>
                      <span>Chapter {chapter.chapterNumber}</span>•
                      <span>
                        {formatDuration(chapter.videoMetaData?.duration)}
                      </span>
                    </div>
                    <div className='text-white text-sm font-medium leading-5'>
                      {chapter.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </ShouldRender>
    </div>
  );
};

export default VideoPlayer;
